import type { MeetingIconProps } from './components/summary/MeetingIcon';
import type { CollapsedMeetingMessageProps } from './components/summary/CollapsedMeetingMessage';
import type { SenderSummaryProps } from './components/summary/SenderSummary';
import type { PreviewSummaryProps } from './components/summary/PreviewSummary';
import type { MeetingDetailsProps } from './components/MeetingDetails';
import type { MeetingCancellationActionProperties } from './components/MeetingCancellationAction';
import type * as lazyIndex from './lazyIndex';
import { createLazyComponent, LazyAction, type LazyComponent, LazyModule } from 'owa-bundling';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "MeetingMessage"*/ './lazyIndex'),
    { name: 'MeetingMessage' }
);

// Export types and schema
export type { MeetingRequestViewState } from './store/schema/MeetingRequestViewState';
export { default as deleteMeetingMessage } from './actions/deleteMeetingMessage';
export { onMeetingActionCompleted } from './actions/onMeetingActionCompleted';
export type { MeetingMessageActionType } from './utils/MeetingMessageActionType';
export type { MeetingRequestViewStateHost } from './store/schema/MeetingRequestViewState';

// Lazy-load actions
export const lazyAddItemToCalendar: LazyAction<
    typeof lazyIndex.addItemToCalendar,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.addItemToCalendar);

export const lazyInitializeMeetingRequestViewState: LazyAction<
    typeof lazyIndex.initializeMeetingRequestViewStateAction,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeMeetingRequestViewStateAction);

export const lazyGetMeetingSummaryProps: LazyAction<
    typeof lazyIndex.getMeetingSummaryProps,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getMeetingSummaryProps);

export const lazyRemoveEventOnCancellation: LazyAction<
    typeof lazyIndex.removeEventOnCancellation,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeEventOnCancellation);

// Lazy-load components (publicly used components only)
export const MeetingDetails: LazyComponent<MeetingDetailsProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MeetingDetails);
export const PreviewSummary: LazyComponent<PreviewSummaryProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.PreviewSummary);
export const SenderSummary: LazyComponent<SenderSummaryProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.SenderSummary
);
export const CollapsedMeetingMessage: LazyComponent<CollapsedMeetingMessageProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CollapsedMeetingMessage);
export const MeetingIcon: LazyComponent<MeetingIconProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.MeetingIcon
);
export const MeetingCancellationAction: LazyComponent<
    MeetingCancellationActionProperties,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.MeetingCancellationAction);

// Lazy-load utilities
export const lazyLogMeetingMessageActionForInvite: LazyAction<
    typeof lazyIndex.logMeetingMessageActionForInvite,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.logMeetingMessageActionForInvite);
export const lazyLogMeetingMessageActionForEvent: LazyAction<
    typeof lazyIndex.logMeetingMessageActionForEvent,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.logMeetingMessageActionForEvent);

export const lazyShowNotificationWhenRespondToMeeting: LazyAction<
    typeof lazyIndex.showNotificationWhenRespondToMeeting,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.showNotificationWhenRespondToMeeting);

export { showAttendeePanelDialog, hideAttendeePanelDialog } from './utils/showAttendeePanelDialog';
